// Shared sequence-logo colour tables and information-content maths.
// Used by both the live <logo-viewer> custom element and the static
// image exporter so the two always compute an identical logo.

export const NUCLEOTIDE_COLORS: Record<string, string> = {
  A: '#00CC00',
  C: '#0000CC',
  G: '#FFB300',
  U: '#CC0000',
};

// WebLogo chemistry color scheme grouped by biochemical property
export const AMINO_ACID_COLORS: Record<string, string> = {
  // Hydrophobic (nonpolar)
  A: '#FF8C00', G: '#FF8C00', V: '#FF8C00', L: '#FF8C00',
  I: '#FF8C00', P: '#FF8C00', F: '#FF8C00', M: '#FF8C00', W: '#FF8C00',
  // Polar uncharged
  S: '#00CC00', T: '#00CC00', C: '#00CC00', Y: '#00CC00',
  N: '#00CC00', Q: '#00CC00',
  // Positively charged
  K: '#0000CC', R: '#0000CC', H: '#0000CC',
  // Negatively charged
  D: '#CC0000', E: '#CC0000',
};

export const TRACKED_NUCLEOTIDES = ['A', 'C', 'G', 'U'];
export const TRACKED_AMINO_ACIDS = ['A', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'V', 'W', 'Y'];
export const AMINO_ACID_ONLY_CHARS = new Set(['D', 'E', 'F', 'H', 'I', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'V', 'W', 'Y']);

export const LOG2_4 = 2;
export const LOG2_20 = Math.log2(20);

export type SeqType = 'rna' | 'protein';

export interface LetterData {
  nucleotide: string;
  /** Fraction of total track height this letter should occupy (0-1) */
  heightFraction: number;
}

export function detectSeqType(sequences: Array<{ sequence: string }>): SeqType {
  let nonNucCount = 0;
  let totalCount = 0;
  for (const seq of sequences) {
    for (const char of seq.sequence.toUpperCase()) {
      if (char === '-' || char === '.' || char === ' ') continue;
      totalCount++;
      if (AMINO_ACID_ONLY_CHARS.has(char)) nonNucCount++;
    }
  }
  return totalCount > 0 && nonNucCount / totalCount > 0.05 ? 'protein' : 'rna';
}

/** Compute per-residue stacked heights for one alignment column (1-indexed position). */
export function computeColumnData(
  sequences: Array<{ sequence: string }>,
  seqType: SeqType,
  pos: number
): LetterData[] {
  const isProtein = seqType === 'protein';
  const alphabet = isProtein ? TRACKED_AMINO_ACIDS : TRACKED_NUCLEOTIDES;
  const maxBits = isProtein ? LOG2_20 : LOG2_4;
  const colIndex = pos - 1;
  const counts: Record<string, number> = {};
  for (const a of alphabet) counts[a] = 0;
  let total = 0;

  for (const seq of sequences) {
    if (colIndex < seq.sequence.length) {
      let char = seq.sequence[colIndex].toUpperCase();
      if (!isProtein && char === 'T') char = 'U';
      if (char in counts) {
        counts[char]++;
        total++;
      }
    }
  }

  if (total === 0) return [];

  // Scale IC by coverage so gap-heavy columns don't produce misleadingly
  // tall logos from a small subset of sequences that happen to agree.
  const coverage = total / sequences.length;

  // Shannon entropy
  let H = 0;
  for (const res of alphabet) {
    const f = counts[res] / total;
    if (f > 0) H -= f * Math.log2(f);
  }
  const IC = Math.max(0, maxBits - H) * coverage;

  const letters: LetterData[] = [];
  for (const res of alphabet) {
    const f = counts[res] / total;
    if (f > 0) {
      letters.push({ nucleotide: res, heightFraction: (f * IC) / maxBits });
    }
  }

  // Smallest letters at the bottom, largest (most conserved) at the top —
  // ascending sort so the rendering loop (which builds bottom-up) places
  // the tallest letter last, leaving it at the top of the stack.
  letters.sort((a, b) => a.heightFraction - b.heightFraction);
  return letters;
}

export function colorForResidue(residue: string, seqType: SeqType): string {
  const colorMap = seqType === 'protein' ? AMINO_ACID_COLORS : NUCLEOTIDE_COLORS;
  return colorMap[residue] || '#999';
}
